/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { focusWithoutScrolling } from "@react-aria/utils";
import {
  mergeProps,
  useButton,
  useFocusRing,
  useHover,
  useLink,
  type AriaButtonProps,
} from "react-aria";
import {
  useRef,
  type ComponentType,
  type MouseEvent,
  type MutableRefObject,
  type PointerEvent,
} from "react";

import { cssFns, useCss, useKeyframes, type Style } from "@superweb/css";

import { useMessage } from "#intl";

import { useUiColors, useUiShadows } from "../theme";
import { useTypo } from "../typo";
import { icons } from "../icons";
import { Tooltip, useTooltip } from "../tooltip";

export type Variant =
  | "default"
  | "action"
  | "floating"
  | "contrast"
  | "outline"
  | "contrastOutline"
  | "ghost";
export type View = Variant;
export type Size = "l" | "m" | "s" | "xs";
export type Shape = "circle" | "squircle";

/**
 * @internal For usage only through wrapper components
 */
export const InternalButton = ({
  ariaLabel,
  disabled = false,
  iconEnd: IconEnd,
  icon: Icon,
  onPress,
  progress = false,
  shape = "squircle",
  size = "m",
  subText,
  text,
  type = "button",
  variant = "default",
  buttonRef,
  ariaButtonProps,
  link,
  tooltip,
  tooltipOptions,
  tabIndex,
}: {
  disabled?: boolean;
  onPress?: () => void;
  progress?: boolean | string;
  shape?: Shape;
  size?: Size;
  type?: "button" | "submit";
  variant?: Variant;
  text?: string;
  subText?: string;
  iconEnd?: ComponentType<{ className?: string }>;
  icon?: ComponentType<{ className?: string }>;
  ariaLabel?: string;
  link?: {
    href: string;
    onClick?: (e: PointerEvent<HTMLAnchorElement>) => void;
    external?: boolean;
    download?: boolean;
  };
  buttonRef?: MutableRefObject<null>;
  ariaButtonProps?: AriaButtonProps<"button">;
  tooltip?: string;
  tooltipOptions?: {
    variant?: "default" | "info";
    placement?: "top" | "bottom" | "start" | "end";
  };
  tabIndex?: number;
}) => {
  const message = useMessage();
  const ref = useRef(null);
  const internalRef = buttonRef || ref;

  const { linkProps, isPressed: isLinkPressed } = useLink(
    {
      isDisabled: disabled,
      "aria-label": ariaLabel,
      onPress: (e) => {
        if (!progress) {
          ariaButtonProps?.onPress?.(e);
          onPress?.();
        }
      },
      href: link?.href,
      download: link?.download,
      ...(link?.external && { target: "_blank", rel: "noreferrer" }),
    },
    internalRef,
  );

  const { buttonProps, isPressed: isButtonPressed } = useButton(
    {
      ...mergeProps(ariaButtonProps, {
        type,
        isDisabled: disabled,
        "aria-label":
          typeof progress === "string"
            ? progress
            : progress
              ? message({
                  id: "01cbe698-49ee-4b20-85d0-4611d1583b71",
                  context:
                    "Button. Loading label (invisible, made for accessibility)",
                  default: "Loading",
                })
              : ariaLabel,
      }),
      onPress: (e) => {
        if (!progress) {
          // Workaround for https://github.com/adobe/react-spectrum/issues/4355
          // @ts-expect-error e.target is not known to be a FocusableElement here, but it is because of useFocus
          focusWithoutScrolling(e.target);
          ariaButtonProps?.onPress?.(e);
          onPress?.();
        }
      },
      // Workaround for https://github.com/adobe/react-spectrum/issues/4355
      preventFocusOnPress: true,
    },
    internalRef,
  );
  const { hoverProps, isHovered } = useHover({
    isDisabled: disabled,
  });
  const { focusProps, isFocusVisible: isFocused } = useFocusRing();
  const {
    triggerProps,
    tooltipProps,
    state: tooltipState,
  } = useTooltip(internalRef);

  const css = useCss();
  const typo = useTypo();
  const uiColors = useUiColors();
  const uiShadows = useUiShadows();
  const keyframes = useKeyframes();

  const isPressed = link ? isLinkPressed : isButtonPressed;
  const transparent = cssFns.transparentize(uiColors.everBack, 1);

  const baseColor = {
    default: uiColors.controlMinor,
    action: uiColors.controlMain,
    outline: progress ? uiColors.background : transparent,
    contrastOutline: transparent,
    floating: uiColors.background,
    contrast: uiColors.everFront,
    ghost: transparent,
  }[variant];

  const textColor = {
    default: uiColors.text,
    action: uiColors.textOnControlMain,
    outline: uiColors.text,
    contrastOutline: uiColors.everFront,
    floating: uiColors.text,
    contrast: uiColors.everBack,
    ghost: uiColors.text,
  }[variant];

  const backgroundColor =
    !(disabled || progress) && (isPressed || isHovered)
      ? cssFns.blend(baseColor, isPressed ? uiColors.press : uiColors.hover)
      : baseColor;

  const iconSpacing = { l: "16px", m: "12px", s: "8px", xs: "6px" }[size];
  const textSpacing = { l: "18px", m: "16px", s: "14px", xs: "12px" }[size];

  const iconSize = size === "xs" ? "20px" : "24px";

  let gridTemplateColumns = "";

  if (Icon && text && IconEnd) {
    gridTemplateColumns = `${iconSpacing} [icon] ${iconSize} auto 4px [text] auto 4px [iconEnd] ${iconSize} auto ${iconSpacing}`;
  } else if (Icon && text) {
    gridTemplateColumns = `${iconSpacing} [icon] ${iconSize} auto 4px [text] auto ${textSpacing}`;
  } else if (text && IconEnd) {
    gridTemplateColumns = `${textSpacing} [text] auto 4px [iconEnd] ${iconSize} auto ${textSpacing}`;
  } else if (Icon && IconEnd) {
    gridTemplateColumns = `${iconSpacing} [icon] ${iconSize} auto ${iconSpacing} [iconEnd] ${iconSize} auto ${iconSpacing}`;
  } else if (Icon) {
    gridTemplateColumns = `${iconSpacing} [icon] ${iconSize} auto ${iconSpacing}`;
  } else if (text) {
    gridTemplateColumns = `${textSpacing} [text] auto ${textSpacing}`;
  } else if (IconEnd) {
    gridTemplateColumns = `auto ${iconSpacing} [iconEnd] ${iconSize} auto ${iconSpacing}`;
  }

  const Content = (
    <span
      css={{
        display: "grid",
        alignContent: "center",
        justifyContent: "center",
        alignItems: "center",
        boxSizing: "border-box",
        position: "relative",
        isolation: "isolate",
        gridTemplateColumns,
        gridTemplateRows: "1fr [content] auto 1fr",
        ...cssFns.overflow("hidden"),
        ...cssFns.border({
          width:
            variant === "outline" || variant === "contrastOutline"
              ? "0.5px"
              : "0",
          style:
            variant === "outline" || variant === "contrastOutline"
              ? "solid"
              : "none",
          color:
            variant === "contrastOutline" ? uiColors.everFront : uiColors.line,
          radius:
            shape === "circle"
              ? "100px"
              : size === "xs"
                ? "10px"
                : size === "s"
                  ? "13px"
                  : "16px",
        }),
        boxShadow: `${
          variant === "floating" && !disabled
            ? uiShadows.bottomNormal
            : "0 0 0 0 transparent"
        }, ${
          isFocused && !disabled
            ? "inset 0 0 0 2px " + uiColors.focus
            : "0 0 0 0 transparent"
        }`,
        cursor: disabled ? "default" : "pointer",
        height: { l: "56px", m: "48px", s: "40px", xs: "32px" }[size],
        backgroundColor,
        color: textColor,
        opacity: disabled ? "0.5" : "1",
        outlineStyle: "none",
      }}
    >
      {Icon && (
        <Icon
          className={css({
            gridColumnStart: "icon",
            gridRowStart: "content",
            width: iconSize,
            height: iconSize,
            display: "flex",
            alignItems: "stretch",
            justifyItems: "stretch",
            opacity: progress ? "0" : "1",
          })}
        />
      )}
      {text && (
        <span
          aria-hidden={progress ? "true" : undefined}
          css={{
            ...typo({
              level: size === "xs" ? "caption1" : "body2",
              weight: variant === "action" ? "medium" : "regular",
              density: "tight",
            }),
            ...cssFns.overflow("hidden"),
            gridColumnStart: "text",
            gridRowStart: "content",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            opacity: progress ? "0" : "1",
          }}
        >
          {text}
          {subText && (
            <span
              css={{
                ...cssFns.overflow("hidden"),
                gridColumnStart: "text",
                gridRowStart: "content",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                ...typo({
                  level: size === "xs" ? "caption2" : "caption1",
                  weight: "regular",
                  density: "tight",
                }),
                display: "block",
              }}
            >
              {subText}
            </span>
          )}
        </span>
      )}
      {IconEnd && (
        <IconEnd
          className={css({
            gridColumnStart: "iconEnd",
            gridRowStart: "content",
            width: iconSize,
            height: iconSize,
            display: "flex",
            alignItems: "stretch",
            justifyItems: "stretch",
            opacity: progress ? "0" : "1",
          })}
        />
      )}

      {progress && (
        <div
          css={{
            position: "absolute",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "0",
            paddingInlineStart: iconSpacing,
            paddingInlineEnd:
              typeof progress === "string" ? textSpacing : iconSpacing,
          }}
        >
          <span
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              animationDuration: "1s",
              animationTimingFunction: "linear",
              animationIterationCount: "infinite",
              animationName: keyframes({
                0: {
                  transform: "rotate(0deg)",
                },
                100: {
                  transform: "rotate(360deg)",
                },
              }),
            }}
          >
            <icons.Spinner width={iconSize} height={iconSize} />
          </span>
          {typeof progress === "string" && (
            <span
              css={{
                display: "flex",
                minWidth: "0",
                marginInlineStart: "4px",
              }}
            >
              <span
                css={{
                  ...typo({
                    level: size === "xs" ? "caption1" : "body2",
                    weight: variant === "action" ? "medium" : "regular",
                    density: "tight",
                  }),
                  ...cssFns.overflow("hidden"),
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {progress}
              </span>
            </span>
          )}
        </div>
      )}

      {tooltip && tooltipState.isOpen && (
        <Tooltip
          state={tooltipState}
          tooltipProps={tooltipProps}
          targetRef={internalRef}
          variant={tooltipOptions?.variant}
          placement={tooltipOptions?.placement}
        >
          {tooltip}
        </Tooltip>
      )}
    </span>
  );

  if (link) {
    const linkStyles: Style = {
      display: "inline-block",
      ...cssFns.margin("0"),
      ...cssFns.padding("0"),
      ...cssFns.border({ style: "none" }),
      outlineStyle: "none",
      backgroundColor: "transparent",
      textAlign: "center",
      textDecorationLine: "none",
    };

    return (
      <a
        {...mergeProps(hoverProps, focusProps, triggerProps, linkProps, {
          tabIndex,
          ...(disabled ? undefined : { onClick: link.onClick }),
        })}
        ref={internalRef}
        css={linkStyles}
      >
        {Content}
      </a>
    );
  }

  return (
    <button
      {...mergeProps(hoverProps, focusProps, triggerProps, buttonProps, {
        tabIndex,
        onClick: (e: MouseEvent<HTMLButtonElement>) => {
          if (progress && type === "submit") {
            e.preventDefault();
          }
        },
      })}
      ref={internalRef}
      css={{
        // Button element cannot be reliably styled in Safari < 14.
        // Hence we use it only as a semantic container with reset styling
        // and mark up the inner layout using span.
        appearance: "none",
        ...cssFns.margin("0"),
        ...cssFns.padding("0"),
        ...cssFns.border({ style: "none" }),
        outlineStyle: "none",
        backgroundColor: "transparent",
      }}
    >
      {Content}
    </button>
  );
};
